import React, { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import DashboardLayout from '../../components/DashboardLayout';
import { 
  FileText, CheckCircle2, Clock, AlertCircle, User, Shield, Heart,
  Phone, Home, FileCheck, ClipboardList, Stethoscope, Activity, Search, ChevronDown
} from 'lucide-react';
import api from '../../api';

const AdmissionPacketIndex = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const patientId = searchParams.get('patientId');
  const [patient, setPatient] = useState(null);
  const [patients, setPatients] = useState([]);
  const [formStatus, setFormStatus] = useState({});
  const [loading, setLoading] = useState(false);
  const [showPatientDropdown, setShowPatientDropdown] = useState(false);
  const [patientSearch, setPatientSearch] = useState('');

  useEffect(() => {
    fetchAllPatients();
  }, []);

  useEffect(() => {
    if (patientId) {
      fetchPatient();
      fetchFormStatus();
    }
  }, [patientId]);

  const fetchAllPatients = async () => {
    try {
      const response = await api.get('/patients');
      if (response.data && response.data.patients) {
        setPatients(response.data.patients);
      }
    } catch (error) {
      console.error('Error fetching patients:', error);
    }
  };

  const fetchPatient = async () => {
    try {
      const response = await api.get(`/patients/${patientId}`);
      if (response.data) {
        setPatient(response.data);
      }
    } catch (error) {
      console.error('Error fetching patient:', error);
    }
  };

  const fetchFormStatus = async () => {
    try {
      const response = await api.get(`/admission-forms/status/${patientId}`);
      if (response.data) {
        setFormStatus(response.data);
      }
    } catch (error) {
      console.error('Error fetching form status:', error);
      setFormStatus({});
    }
  };

  const handlePatientSelect = (selectedPatient) => {
    setPatient(selectedPatient);
    setSearchParams({ patientId: selectedPatient.id });
    setShowPatientDropdown(false);
    setPatientSearch('');
  };

  const filteredPatients = patients.filter(p => {
    const searchLower = patientSearch.toLowerCase();
    const fullName = `${p.firstName} ${p.lastName}`.toLowerCase();
    return fullName.includes(searchLower) || 
           (p.phone && p.phone.includes(patientSearch)) ||
           (p.email && p.email.toLowerCase().includes(searchLower));
  });

  const forms = [
    {
      category: 'Welcome & Introduction',
      icon: Home,
      color: 'bg-blue-500',
      items: [
        { name: 'Table of Contents', href: `/dashboard/admission/toc`, key: 'toc' },
        { name: 'Welcome Letter', href: `/dashboard/admission/welcome`, key: 'welcome' },
        { name: 'Admission Criteria', href: `/dashboard/admission/criteria`, key: 'criteria' },
      ]
    },
    {
      category: 'Rights & Responsibilities',
      icon: Shield,
      color: 'bg-purple-500',
      items: [
        { name: 'Client Rights & Responsibilities', href: `/dashboard/admission/rights`, key: 'rights' },
        { name: 'Agency Responsibilities', href: `/dashboard/admission/agency-responsibilities`, key: 'agency_resp' },
        { name: 'Complaints & Grievances', href: `/dashboard/admission/grievances`, key: 'grievances' },
        { name: 'Rights of the Elderly', href: `/dashboard/admission/elderly-rights`, key: 'elderly_rights' },
      ]
    },
    {
      category: 'Privacy & Compliance',
      icon: FileCheck,
      color: 'bg-green-500',
      items: [
        { name: 'HIPAA Notice of Privacy Practices', href: `/dashboard/admission/hipaa`, key: 'hipaa' },
        { name: 'Medicaid Fraud Reporting', href: `/dashboard/admission/fraud-reporting`, key: 'fraud' },
        { name: 'Abuse, Neglect & Exploitation Policy', href: `/dashboard/admission/abuse-policy`, key: 'abuse' },
      ]
    },
    {
      category: 'Health Directives',
      icon: Heart,
      color: 'bg-red-500',
      items: [
        { name: 'Advance Directives', href: `/dashboard/admission/advance-directives`, key: 'advance_directives' },
        { name: 'Infection Control', href: `/dashboard/admission/infection-control`, key: 'infection' },
        { name: 'COVID-19 Screening', href: `/dashboard/admission/covid-screening`, key: 'covid' },
      ]
    },
    {
      category: 'Emergency Preparedness',
      icon: AlertCircle,
      color: 'bg-orange-500',
      items: [
        { name: 'Family Disaster Plan', href: `/dashboard/admission/disaster-plan`, key: 'disaster' },
        { name: 'Emergency Telephone Numbers', href: `/dashboard/admission/emergency-numbers`, key: 'emergency_numbers' },
        { name: 'Home Safety Guidelines', href: `/dashboard/admission/home-safety`, key: 'home_safety' },
        { name: 'Tornado Preparedness', href: `/dashboard/admission/tornado-plan`, key: 'tornado' },
        { name: 'Evacuation Assistance', href: `/dashboard/admission/evacuation`, key: 'evacuation' },
        { name: 'Emergency Preparedness & Risk Assessment', href: `/dashboard/admission/risk-assessment`, key: 'risk_assessment' },
      ]
    },
    {
      category: 'Billing & Payment',
      icon: FileText,
      color: 'bg-teal-500',
      items: [
        { name: 'Payment of Services / Billing Agreement', href: `/dashboard/admission/billing-agreement`, key: 'billing' },
        { name: 'Service Plan Supervision', href: `/dashboard/admission/service-plan`, key: 'service_plan' },
      ]
    },
    {
      category: 'Consent & Verification',
      icon: ClipboardList,
      color: 'bg-indigo-500',
      items: [
        { name: 'Consent & Verification of Receipt', href: `/dashboard/admission/consent-verification`, key: 'consent' },
        { name: 'Emergency Information Form', href: `/dashboard/admission/emergency-info`, key: 'emergency_info' },
      ]
    },
    {
      category: 'Clinical Assessment',
      icon: Stethoscope,
      color: 'bg-pink-500',
      items: [
        { name: 'Personal Care Home Assessment', href: `/dashboard/admission/home-assessment`, key: 'home_assessment' },
        { name: 'Medication Profile', href: `/dashboard/clinical/medication-profile`, key: 'medication' },
        { name: 'Vital Signs Record', href: `/dashboard/admission/vital-signs`, key: 'vital_signs' },
        { name: 'Care Plan & Progress Notes', href: `/dashboard/clinical/poc-485`, key: 'care_plan' },
        { name: 'Client Calendar', href: `/dashboard/scheduling`, key: 'calendar' },
        { name: 'Communication Sheet', href: `/dashboard/patients/communication`, key: 'communication' },
      ]
    },
  ];

  const getStatusBadge = (key) => {
    const status = formStatus[key];
    if (status === 'complete') {
      return <CheckCircle2 className="w-5 h-5 text-green-500" />;
    } else if (status === 'in_progress') {
      return <Clock className="w-5 h-5 text-yellow-500" />;
    }
    return <div className="w-5 h-5 rounded-full border-2 border-gray-300" />;
  };

  const completedCount = Object.values(formStatus).filter(s => s === 'complete').length;
  const totalForms = forms.reduce((acc, cat) => acc + cat.items.length, 0);

  const getFormHref = (baseHref) => {
    if (patientId) {
      return `${baseHref}?patientId=${patientId}`;
    }
    return baseHref;
  };

  return (
    <DashboardLayout>
      <div className="max-w-7xl mx-auto" data-testid="admission-packet-index">
        {/* Header */}
        <div className="bg-gradient-to-r from-[#2c3e50] to-[#34495e] rounded-xl p-6 mb-6 text-white">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <h1 className="text-2xl font-bold mb-2">Admission Packet</h1>
              <p className="text-gray-300">
                Complete all required admission forms for patient onboarding
              </p>
            </div>
            
            {/* Patient Selector */}
            <div className="relative">
              <button
                onClick={() => setShowPatientDropdown(!showPatientDropdown)}
                className="flex items-center gap-3 bg-white/10 hover:bg-white/20 rounded-lg px-4 py-3 transition-colors min-w-[280px]"
                data-testid="patient-selector-btn"
              >
                {patient ? (
                  <>
                    <div className="w-10 h-10 bg-[#f39c12] rounded-full flex items-center justify-center text-white font-bold">
                      {patient.firstName?.[0]}{patient.lastName?.[0]}
                    </div>
                    <div className="text-left flex-1">
                      <p className="font-semibold">{patient.firstName} {patient.lastName}</p>
                      <p className="text-sm text-gray-300">{patient.phone || patient.email || 'No contact'}</p>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="w-10 h-10 bg-gray-500 rounded-full flex items-center justify-center">
                      <User className="w-5 h-5 text-white" />
                    </div>
                    <div className="text-left flex-1">
                      <p className="font-semibold">Select a Patient</p>
                      <p className="text-sm text-gray-300">Click to choose patient</p>
                    </div>
                  </>
                )}
                <ChevronDown className={`w-5 h-5 transition-transform ${showPatientDropdown ? 'rotate-180' : ''}`} />
              </button>
              
              {/* Dropdown */}
              {showPatientDropdown && (
                <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-lg shadow-xl z-50 max-h-96 overflow-hidden">
                  {/* Search */}
                  <div className="p-3 border-b">
                    <div className="relative">
                      <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                      <input
                        type="text"
                        value={patientSearch}
                        onChange={(e) => setPatientSearch(e.target.value)}
                        placeholder="Search patients..."
                        className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-gray-900 text-sm focus:ring-2 focus:ring-[#f39c12] focus:border-transparent"
                        data-testid="patient-search-input"
                      />
                    </div>
                  </div>
                  
                  {/* Patient List */}
                  <div className="max-h-72 overflow-y-auto">
                    {filteredPatients.length === 0 ? (
                      <div className="p-4 text-center text-gray-500">
                        No patients found
                      </div>
                    ) : (
                      filteredPatients.map((p) => (
                        <button
                          key={p.id}
                          onClick={() => handlePatientSelect(p)}
                          className={`w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors text-left ${
                            patientId === p.id ? 'bg-orange-50' : ''
                          }`}
                          data-testid={`patient-option-${p.id}`}
                        >
                          <div className="w-10 h-10 bg-[#f39c12] rounded-full flex items-center justify-center text-white font-bold text-sm">
                            {p.firstName?.[0]}{p.lastName?.[0]}
                          </div>
                          <div className="flex-1">
                            <p className="font-medium text-gray-900">{p.firstName} {p.lastName}</p>
                            <p className="text-sm text-gray-500">{p.phone || p.email || 'No contact info'}</p>
                          </div>
                          {patientId === p.id && (
                            <CheckCircle2 className="w-5 h-5 text-green-500" />
                          )}
                        </button>
                      ))
                    )}
                  </div>
                  
                  {/* Add New Patient Link */}
                  <div className="p-3 border-t bg-gray-50">
                    <Link
                      to="/dashboard/patients/demographics"
                      className="flex items-center justify-center gap-2 w-full py-2 text-[#f39c12] hover:text-[#e67e22] font-medium text-sm"
                    >
                      <User className="w-4 h-4" />
                      Add New Patient
                    </Link>
                  </div>
                </div>
              )}
            </div>
          </div>
          
          {/* Progress Bar */}
          {patient && (
            <div className="mt-6">
              <div className="flex justify-between text-sm mb-2">
                <span>Progress</span>
                <span>{completedCount} of {totalForms} forms completed</span>
              </div>
              <div className="w-full bg-white/20 rounded-full h-3">
                <div 
                  className="bg-[#f39c12] h-3 rounded-full transition-all duration-500"
                  style={{ width: `${(completedCount / totalForms) * 100}%` }}
                />
              </div>
            </div>
          )}
        </div>

        {/* Patient Selection Notice (if no patient) */}
        {!patient && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 mb-6">
            <div className="flex items-center gap-3">
              <AlertCircle className="w-6 h-6 text-yellow-600" />
              <div>
                <p className="font-semibold text-yellow-800">Select a Patient</p>
                <p className="text-sm text-yellow-700">
                  Use the patient selector above to choose a patient, or{' '}
                  <Link to="/dashboard/patients/demographics" className="underline hover:text-yellow-900 font-medium">
                    add a new patient
                  </Link>{' '}
                  from the Patient Demographics page.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Forms Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {forms.map((category, catIndex) => (
            <div key={catIndex} className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              <div className={`${category.color} px-4 py-3 flex items-center gap-3`}>
                <category.icon className="w-5 h-5 text-white" />
                <h2 className="text-white font-semibold">{category.category}</h2>
              </div>
              <div className="divide-y divide-gray-100">
                {category.items.map((item, itemIndex) => (
                  <Link
                    key={itemIndex}
                    to={patient ? getFormHref(item.href) : '#'}
                    className={`flex items-center justify-between px-4 py-3 hover:bg-gray-50 transition-colors ${
                      !patient ? 'opacity-50 cursor-not-allowed' : ''
                    }`}
                    onClick={(e) => !patient && e.preventDefault()}
                    data-testid={`form-link-${item.key}`}
                  >
                    <span className="text-gray-700">{item.name}</span>
                    {getStatusBadge(item.key)}
                  </Link>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </DashboardLayout>
  );
};

export default AdmissionPacketIndex;
